/**
 * UploadContext - Global upload state management
 * Feature: 847 - Upload navigation persistence
 *
 * Provides global state for batch uploads that persist across navigation.
 * This allows users to navigate away from a collection while uploads continue.
 *
 * Key features:
 * - Uploads continue when navigating within the app
 * - beforeunload warning when closing browser tab with active uploads
 * - Global BatchUploadProgress rendering
 * - Session-based upload management (keyed by collection ID)
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode
} from 'react';
import type {
  FileUploadItem,
  BatchUploadState,
  UploadAction,
  UseBatchUploadOptions,
  CompletedBatchInfo
} from '@/types/upload';
import {
  calculateBatchState,
  getNextFilesToStart,
  createFileUploadItem,
  MAX_CONCURRENT_UPLOADS
} from '@/utils/uploadQueue';
import { filterValidFiles, generatePhotoTitle } from '@/utils/fileValidation';
import { BatchUploadProgress } from '@/components/photos/BatchUploadProgress';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useAddPhotoToCollectionGraphQL } from '@/hooks/graphql/useCollectionMutationsGraphQL';
import logger from '@/utils/logger';

/**
 * Individual upload session for a collection
 */
interface UploadSession {
  /** Collection ID this session is uploading to */
  collectionId: string;
  /** Current batch upload state */
  state: BatchUploadState;
  /** Starting position for photos in this session */
  startingPosition: number;
  /** Next position to assign */
  nextPosition: number;
  /** Whether batch completion has been processed */
  batchCompletionProcessed: boolean;
  /** Active upload abort controllers */
  activeUploads: Map<string, AbortController>;
}

/**
 * Callbacks stored per session (in refs to avoid stale closures)
 */
interface SessionCallbacks {
  reorderPhotos?: (photoIds: string[]) => Promise<void>;
  onBatchComplete?: (info: CompletedBatchInfo) => void;
}

/**
 * Context value interface
 */
interface UploadContextValue {
  /**
   * Get the upload session for a collection.
   * Returns null if no session exists.
   */
  getSession: (collectionId: string) => UploadSession | null;

  /**
   * Create or update a session for a collection.
   * If session exists, updates the callbacks only.
   */
  initSession: (
    collectionId: string,
    startingPosition: number,
    options?: UseBatchUploadOptions
  ) => void;

  /**
   * Add files to an upload session
   */
  addFiles: (collectionId: string, files: File[]) => void;

  /**
   * Retry a failed upload
   */
  retryUpload: (collectionId: string, id: string) => void;

  /**
   * Remove a file from the queue
   */
  removeFile: (collectionId: string, id: string) => void;

  /**
   * Clear completed uploads from a session
   */
  clearCompleted: (collectionId: string) => void;

  /**
   * Clear all completed sessions entirely
   */
  clearAllCompleted: () => void;

  /**
   * Whether any uploads are currently active across all sessions
   */
  hasActiveUploads: boolean;

  /**
   * Total count of files being uploaded across all sessions
   */
  totalActiveCount: number;

  /**
   * Check if uploads are active for a different collection than the specified one.
   * Returns true if there are active/pending uploads for ANY collection other than the given one.
   * Useful for disabling upload UI when another collection's uploads are in progress.
   */
  hasUploadsForOtherCollection: (currentCollectionId: string | null) => boolean;
}

/**
 * Session action types
 */
type SessionAction =
  | { type: 'INIT_SESSION'; payload: { collectionId: string; startingPosition: number } }
  | { type: 'UPDATE_SESSION_STATE'; payload: { collectionId: string; state: BatchUploadState } }
  | { type: 'UPDATE_NEXT_POSITION'; payload: { collectionId: string; nextPosition: number } }
  | { type: 'MARK_BATCH_PROCESSED'; payload: { collectionId: string } }
  | { type: 'RESET_BATCH_PROCESSED'; payload: { collectionId: string } }
  | { type: 'REMOVE_SESSION'; payload: { collectionId: string } };

/**
 * Sessions state type
 */
type SessionsState = Map<string, UploadSession>;

/**
 * Upload reducer for individual file state
 */
function uploadReducer(state: BatchUploadState, action: UploadAction): BatchUploadState {
  let newFiles: FileUploadItem[];

  switch (action.type) {
    case 'ADD_FILES': {
      const { valid } = filterValidFiles(action.payload.files);
      const newItems = valid.map(file => createFileUploadItem(file));
      newFiles = [...state.files, ...newItems];
      return calculateBatchState(newFiles);
    }

    case 'ADD_ITEMS': {
      newFiles = [...state.files, ...action.payload.items];
      return calculateBatchState(newFiles);
    }

    case 'START_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? { ...file, state: 'uploading' as const, startedAt: action.payload.startedAt }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'UPDATE_PROGRESS': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? { ...file, progress: Math.min(100, Math.max(0, action.payload.progress)) }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'COMPLETE_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? {
              ...file,
              state: 'success' as const,
              photoId: action.payload.photoId,
              completedAt: action.payload.completedAt,
              progress: 100
            }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'FAIL_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id
          ? {
              ...file,
              state: 'error' as const,
              error: action.payload.error,
              completedAt: action.payload.completedAt
            }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'RETRY_UPLOAD': {
      newFiles = state.files.map(file =>
        file.id === action.payload.id && file.state === 'error'
          ? {
              ...file,
              state: 'pending' as const,
              error: null,
              progress: 0,
              startedAt: null,
              completedAt: null
            }
          : file
      );
      return calculateBatchState(newFiles);
    }

    case 'REMOVE_FILE': {
      newFiles = state.files.filter(
        file => file.id !== action.payload.id || file.state === 'uploading'
      );
      return calculateBatchState(newFiles);
    }

    default:
      return state;
  }
}

/**
 * Sessions reducer
 */
function sessionsReducer(state: SessionsState, action: SessionAction): SessionsState {
  switch (action.type) {
    case 'INIT_SESSION': {
      const { collectionId, startingPosition } = action.payload;
      // Only create new state if session doesn't exist
      if (state.has(collectionId)) {
        return state; // No change needed
      }
      const newState = new Map(state);
      newState.set(collectionId, {
        collectionId,
        state: calculateBatchState([]),
        startingPosition,
        nextPosition: startingPosition,
        batchCompletionProcessed: false,
        activeUploads: new Map()
      });
      return newState;
    }

    case 'UPDATE_SESSION_STATE': {
      const { collectionId, state: batchState } = action.payload;
      const session = state.get(collectionId);
      if (!session) return state;
      const newState = new Map(state);
      newState.set(collectionId, { ...session, state: batchState });
      return newState;
    }

    case 'UPDATE_NEXT_POSITION': {
      const { collectionId, nextPosition } = action.payload;
      const session = state.get(collectionId);
      if (!session) return state;
      const newState = new Map(state);
      newState.set(collectionId, { ...session, nextPosition });
      return newState;
    }

    case 'MARK_BATCH_PROCESSED': {
      const { collectionId } = action.payload;
      const session = state.get(collectionId);
      if (!session) return state;
      const newState = new Map(state);
      newState.set(collectionId, { ...session, batchCompletionProcessed: true });
      return newState;
    }

    case 'RESET_BATCH_PROCESSED': {
      const { collectionId } = action.payload;
      const session = state.get(collectionId);
      if (!session) return state;
      const newState = new Map(state);
      newState.set(collectionId, { ...session, batchCompletionProcessed: false });
      return newState;
    }

    case 'REMOVE_SESSION': {
      if (!state.has(action.payload.collectionId)) return state;
      const newState = new Map(state);
      newState.delete(action.payload.collectionId);
      return newState;
    }

    default:
      return state;
  }
}

/**
 * Initial empty sessions state
 */
const initialSessionsState: SessionsState = new Map();

/**
 * Context instance
 */
const UploadContext = createContext<UploadContextValue | null>(null);

/**
 * Provider props
 */
interface UploadProviderProps {
  children: ReactNode;
}

/**
 * UploadProvider - Manages global upload state
 *
 * Wraps the app to provide upload state persistence across navigation.
 * Renders BatchUploadProgress globally for all active sessions.
 *
 * @example
 * ```tsx
 * <UploadProvider>
 *   <App />
 * </UploadProvider>
 * ```
 */
export function UploadProvider({ children }: UploadProviderProps) {
  const [sessions, dispatchSessions] = useReducer(sessionsReducer, initialSessionsState);
  const { uploadPhoto } = usePhotoUpload();
  const { addPhotoToCollection } = useAddPhotoToCollectionGraphQL();

  // Store callbacks in refs to avoid stale closures
  const callbacksRef = useRef<Map<string, SessionCallbacks>>(new Map());

  // Compute derived state
  const hasActiveUploads = Array.from(sessions.values()).some(
    session => session.state.activeCount > 0 || session.state.pendingCount > 0
  );

  const totalActiveCount = Array.from(sessions.values()).reduce(
    (sum, session) => sum + session.state.activeCount + session.state.pendingCount,
    0
  );

  // Combine all sessions into a single state for UI (memoized for performance)
  const combinedState = useMemo<BatchUploadState>(() => {
    const initialBatchState: BatchUploadState = {
      files: [],
      activeCount: 0,
      pendingCount: 0,
      completedCount: 0,
      failedCount: 0,
      totalCount: 0,
      overallProgress: 0,
      isComplete: true,
      hasFailures: false
    };

    return Array.from(sessions.values()).reduce<BatchUploadState>(
      (acc, session) => ({
        files: [...acc.files, ...session.state.files],
        activeCount: acc.activeCount + session.state.activeCount,
        pendingCount: acc.pendingCount + session.state.pendingCount,
        completedCount: acc.completedCount + session.state.completedCount,
        failedCount: acc.failedCount + session.state.failedCount,
        totalCount: acc.totalCount + session.state.totalCount,
        overallProgress:
          acc.totalCount + session.state.totalCount > 0
            ? Math.round(
                ((acc.completedCount + acc.failedCount + session.state.completedCount + session.state.failedCount) /
                  (acc.totalCount + session.state.totalCount)) *
                  100
              )
            : 0,
        isComplete:
          acc.totalCount + session.state.totalCount > 0
            ? acc.completedCount +
                acc.failedCount +
                session.state.completedCount +
                session.state.failedCount ===
              acc.totalCount + session.state.totalCount
            : true,
        hasFailures: acc.hasFailures || session.state.hasFailures
      }),
      initialBatchState
    );
  }, [sessions]);

  /**
   * Process an individual file upload
   */
  const processUpload = useCallback(
    async (collectionId: string, item: FileUploadItem, session: UploadSession) => {
      const abortController = new AbortController();
      session.activeUploads.set(item.id, abortController);

      logger.log('UploadContext', 'debug', `Starting upload for item: ${item.filename}`, {
        itemId: item.id,
        filename: item.filename,
        collectionId
      });

      try {
        const title = generatePhotoTitle(item.filename);

        const result = await uploadPhoto({
          title,
          file: item.file
        });

        if (abortController.signal.aborted) {
          logger.log('UploadContext', 'warn', `Upload aborted for: ${item.filename}`);
          return;
        }

        if (!result.success || !result.photoId) {
          throw new Error(result.error || 'Upload failed');
        }

        const photoId = result.photoId;
        logger.log('UploadContext', 'info', `Photo uploaded successfully: ${item.filename}`, { photoId });

        // Add photo to collection
        const position = item.intendedPosition;
        if (position === null) {
          throw new Error('No position assigned for file');
        }

        await addPhotoToCollection({
          collectionId,
          photoId,
          displayOrder: position
        });

        // Update state
        const newState = uploadReducer(session.state, {
          type: 'COMPLETE_UPLOAD',
          payload: {
            id: item.id,
            photoId,
            completedAt: new Date()
          }
        });

        dispatchSessions({
          type: 'UPDATE_SESSION_STATE',
          payload: { collectionId, state: newState }
        });
      } catch (error: unknown) {
        if (abortController.signal.aborted) {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        logger.log('UploadContext', 'error', `Upload failed for: ${item.filename}`, { error });

        const newState = uploadReducer(session.state, {
          type: 'FAIL_UPLOAD',
          payload: {
            id: item.id,
            error: errorMessage,
            completedAt: new Date()
          }
        });

        dispatchSessions({
          type: 'UPDATE_SESSION_STATE',
          payload: { collectionId, state: newState }
        });
      } finally {
        session.activeUploads.delete(item.id);
      }
    },
    [uploadPhoto, addPhotoToCollection]
  );

  // Track uploads that have been started to prevent duplicates
  const startedUploadsRef = useRef<Set<string>>(new Set());

  /**
   * Queue processor effect - starts pending uploads when slots available
   */
  useEffect(() => {
    sessions.forEach((session, collectionId) => {
      const filesToStart = getNextFilesToStart(session.state.files, MAX_CONCURRENT_UPLOADS);

      if (filesToStart.length > 0) {
        // Filter out uploads that have already been started (prevents race condition duplicates)
        const newFilesToStart = filesToStart.filter(f => !startedUploadsRef.current.has(f.id));

        if (newFilesToStart.length === 0) {
          return;
        }

        logger.log('UploadContext', 'debug', `Starting ${newFilesToStart.length} uploads for ${collectionId}`);

        // Mark all as started BEFORE dispatching to prevent race conditions
        newFilesToStart.forEach(file => {
          startedUploadsRef.current.add(file.id);
        });

        // Accumulate state changes properly
        let currentState = session.state;
        newFilesToStart.forEach(file => {
          currentState = uploadReducer(currentState, {
            type: 'START_UPLOAD',
            payload: { id: file.id, startedAt: new Date() }
          });
        });

        // Single dispatch with all changes
        dispatchSessions({
          type: 'UPDATE_SESSION_STATE',
          payload: { collectionId, state: currentState }
        });

        // Process uploads asynchronously
        newFilesToStart.forEach(file => {
          processUpload(collectionId, file, session);
        });
      }
    });
  }, [sessions, processUpload]);

  // Clean up started uploads ref when uploads complete or fail
  useEffect(() => {
    sessions.forEach(session => {
      session.state.files.forEach(file => {
        if (file.state === 'success' || file.state === 'error') {
          startedUploadsRef.current.delete(file.id);
        }
      });
    });
  }, [sessions]);

  /**
   * Batch completion effect - verify and fix order after all uploads complete
   */
  useEffect(() => {
    sessions.forEach((session, collectionId) => {
      if (!session.state.isComplete || session.state.totalCount === 0) {
        return;
      }

      if (session.batchCompletionProcessed) {
        return;
      }

      dispatchSessions({
        type: 'MARK_BATCH_PROCESSED',
        payload: { collectionId }
      });

      const successfulUploads = session.state.files
        .filter(
          (f): f is FileUploadItem & { photoId: string; intendedPosition: number } =>
            f.state === 'success' && f.photoId !== null && f.intendedPosition !== null
        )
        .map(f => ({
          photoId: f.photoId,
          intendedPosition: f.intendedPosition,
          filename: f.filename
        }))
        .sort((a, b) => a.intendedPosition - b.intendedPosition);

      const batchInfo: CompletedBatchInfo = {
        successfulUploads,
        failedCount: session.state.failedCount,
        totalCount: session.state.totalCount
      };

      logger.info('UploadContext', 'Batch upload completed', {
        collectionId,
        successCount: successfulUploads.length,
        failedCount: session.state.failedCount
      });

      // Call callbacks
      const callbacks = callbacksRef.current.get(collectionId);
      callbacks?.onBatchComplete?.(batchInfo);

      if (successfulUploads.length > 1 && callbacks?.reorderPhotos) {
        const orderedPhotoIds = successfulUploads.map(u => u.photoId);
        callbacks.reorderPhotos(orderedPhotoIds).catch(error => {
          logger.error('UploadContext', 'Failed to verify/fix upload order', { error });
        });
      }
    });
  }, [sessions]);

  /**
   * beforeunload warning when uploads are active
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads) {
        e.preventDefault();
        // Modern browsers require returnValue to be set
        e.returnValue = 'You have uploads in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads]);

  // Context methods
  const getSession = useCallback(
    (collectionId: string): UploadSession | null => {
      return sessions.get(collectionId) || null;
    },
    [sessions]
  );

  const initSession = useCallback(
    (collectionId: string, startingPosition: number, options?: UseBatchUploadOptions) => {
      // Initialize session if it doesn't exist
      dispatchSessions({
        type: 'INIT_SESSION',
        payload: { collectionId, startingPosition }
      });

      // Update callbacks
      callbacksRef.current.set(collectionId, {
        reorderPhotos: options?.reorderPhotos,
        onBatchComplete: options?.onBatchComplete
      });
    },
    []
  );

  const addFiles = useCallback(
    (collectionId: string, files: File[]) => {
      const session = sessions.get(collectionId);
      if (!session) {
        logger.error('UploadContext', 'Cannot add files - session not found', { collectionId });
        return;
      }

      // Warn for very large batches
      if (files.length >= 50) {
        logger.warn('UploadContext', `Large batch detected: ${files.length} files`);
      }

      const { valid } = filterValidFiles(files);
      if (valid.length === 0) return;

      // Reset batch completion flag
      dispatchSessions({
        type: 'RESET_BATCH_PROCESSED',
        payload: { collectionId }
      });

      // Assign positions
      const items = valid.map((file, index) => {
        const position = session.nextPosition + index;
        return createFileUploadItem(file, position);
      });

      // Update next position
      dispatchSessions({
        type: 'UPDATE_NEXT_POSITION',
        payload: { collectionId, nextPosition: session.nextPosition + valid.length }
      });

      // Add items to state
      const newState = uploadReducer(session.state, {
        type: 'ADD_ITEMS',
        payload: { items }
      });

      dispatchSessions({
        type: 'UPDATE_SESSION_STATE',
        payload: { collectionId, state: newState }
      });
    },
    [sessions]
  );

  const retryUpload = useCallback(
    (collectionId: string, id: string) => {
      const session = sessions.get(collectionId);
      if (!session) return;

      const newState = uploadReducer(session.state, {
        type: 'RETRY_UPLOAD',
        payload: { id }
      });

      dispatchSessions({
        type: 'UPDATE_SESSION_STATE',
        payload: { collectionId, state: newState }
      });
    },
    [sessions]
  );

  const removeFile = useCallback(
    (collectionId: string, id: string) => {
      const session = sessions.get(collectionId);
      if (!session) return;

      const newState = uploadReducer(session.state, {
        type: 'REMOVE_FILE',
        payload: { id }
      });

      dispatchSessions({
        type: 'UPDATE_SESSION_STATE',
        payload: { collectionId, state: newState }
      });
    },
    [sessions]
  );

  const clearCompleted = useCallback(
    (collectionId: string) => {
      const session = sessions.get(collectionId);
      if (!session) return;

      // If all files are complete, remove the session entirely
      if (session.state.isComplete) {
        dispatchSessions({
          type: 'REMOVE_SESSION',
          payload: { collectionId }
        });
        callbacksRef.current.delete(collectionId);
      } else {
        // Otherwise just remove completed/failed files
        let newState = session.state;
        session.state.files
          .filter(f => f.state === 'success' || f.state === 'error')
          .forEach(file => {
            newState = uploadReducer(newState, {
              type: 'REMOVE_FILE',
              payload: { id: file.id }
            });
          });

        dispatchSessions({
          type: 'UPDATE_SESSION_STATE',
          payload: { collectionId, state: newState }
        });
      }
    },
    [sessions]
  );

  const clearAllCompleted = useCallback(() => {
    sessions.forEach((session, collectionId) => {
      if (session.state.isComplete) {
        dispatchSessions({
          type: 'REMOVE_SESSION',
          payload: { collectionId }
        });
        callbacksRef.current.delete(collectionId);
      }
    });
  }, [sessions]);

  // Handle retry from combined UI
  const handleRetry = useCallback(
    (id: string) => {
      // Find which session this file belongs to
      for (const [collectionId, session] of sessions) {
        if (session.state.files.some(f => f.id === id)) {
          retryUpload(collectionId, id);
          return;
        }
      }
    },
    [sessions, retryUpload]
  );

  // Handle remove from combined UI
  const handleRemove = useCallback(
    (id: string) => {
      // Find which session this file belongs to
      for (const [collectionId, session] of sessions) {
        if (session.state.files.some(f => f.id === id)) {
          removeFile(collectionId, id);
          return;
        }
      }
    },
    [sessions, removeFile]
  );

  // Check if uploads are active for any collection OTHER than the specified one
  const hasUploadsForOtherCollection = useCallback(
    (currentCollectionId: string | null): boolean => {
      for (const [collectionId, session] of sessions) {
        // Skip the current collection
        if (collectionId === currentCollectionId) continue;
        // Check if this other collection has active or pending uploads
        if (session.state.activeCount > 0 || session.state.pendingCount > 0) {
          return true;
        }
      }
      return false;
    },
    [sessions]
  );

  const value: UploadContextValue = {
    getSession,
    initSession,
    addFiles,
    retryUpload,
    removeFile,
    clearCompleted,
    clearAllCompleted,
    hasActiveUploads,
    totalActiveCount,
    hasUploadsForOtherCollection
  };

  return (
    <UploadContext.Provider value={value}>
      {children}

      {/* Global upload progress UI */}
      {combinedState.totalCount > 0 && (
        <BatchUploadProgress
          batchState={combinedState}
          onRetry={handleRetry}
          onRemove={handleRemove}
          onClearCompleted={clearAllCompleted}
        />
      )}
    </UploadContext.Provider>
  );
}

/**
 * Default context value for when used outside UploadProvider
 * All methods are no-ops and all state indicates no active uploads
 */
const defaultContextValue: UploadContextValue = {
  getSession: () => null,
  initSession: () => {},
  addFiles: () => {},
  retryUpload: () => {},
  removeFile: () => {},
  clearCompleted: () => {},
  clearAllCompleted: () => {},
  hasActiveUploads: false,
  totalActiveCount: 0,
  hasUploadsForOtherCollection: () => false
};

/**
 * useUploadContext - Access global upload state
 *
 * @returns UploadContextValue (returns safe defaults if used outside UploadProvider)
 */
export function useUploadContext(): UploadContextValue {
  const context = useContext(UploadContext);

  // Return safe defaults if not in a provider (e.g., in tests)
  if (!context) {
    return defaultContextValue;
  }

  return context;
}

/**
 * useBatchUploadGlobal - Hook for batch uploads that persist across navigation
 *
 * This is a drop-in replacement for useBatchUpload that uses global state.
 *
 * @param collectionId - Collection ID to upload to
 * @param startingPosition - Starting position for photos (defaults to 1)
 * @param options - Optional callbacks for order verification
 * @returns State and control functions similar to useBatchUpload
 */
export function useBatchUploadGlobal(
  collectionId: string,
  startingPosition: number = 1,
  options?: UseBatchUploadOptions
) {
  const context = useUploadContext();

  // Use ref for options to avoid re-renders - callbacks are stored in refs in the provider
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Initialize session on mount - only depends on collectionId and startingPosition
  // Options are passed via ref to avoid infinite loops from callback recreation
  useEffect(() => {
    context.initSession(collectionId, startingPosition, optionsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, startingPosition]);

  // Get current session state
  const session = context.getSession(collectionId);
  const state = session?.state ?? calculateBatchState([]);

  // Bind methods to this collection
  const addFiles = useCallback(
    (files: File[]) => context.addFiles(collectionId, files),
    [context, collectionId]
  );

  const retryUpload = useCallback(
    (id: string) => context.retryUpload(collectionId, id),
    [context, collectionId]
  );

  const removeFile = useCallback(
    (id: string) => context.removeFile(collectionId, id),
    [context, collectionId]
  );

  const clearCompleted = useCallback(
    () => context.clearCompleted(collectionId),
    [context, collectionId]
  );

  // Check if uploads are blocked because another collection has active uploads
  const uploadsBlocked = context.hasUploadsForOtherCollection(collectionId);

  // Session is ready when it exists in the context
  const sessionReady = session !== null;

  return {
    state,
    addFiles,
    retryUpload,
    removeFile,
    clearCompleted,
    /** True if uploads to this collection are blocked because another collection has active uploads */
    uploadsBlocked,
    /** True when the session is initialized and ready to receive files */
    sessionReady
  };
}
